import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  GitBranch, Save, RefreshCw, AlertTriangle, Info, Plus, Trash2, X, ChevronUp, ChevronDown,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../lib/firebase';
import { LeadTerritoryEditor } from '../../components/leads/LeadTerritoryEditor';
import type { TerritoryOffice } from '../../components/leads/territoryMapData';
// VALUE import via the dist subpath, never the '@valutten/shared-schemas'
// barrel: the barrel pulls Node crypto into the browser bundle (see
// OwnerSelect.tsx). Types below are erased, so the barrel is fine for those.
import { isTerritoryRoutingEnabled } from '@valutten/shared-schemas/dist/leadTerritories';
import type { LeadRoutingRules, PostcodeTerritory } from '@valutten/shared-schemas';
import { AU_TIMEZONE_VALUES, AU_TIMEZONE_LABELS } from '@valutten/shared-schemas/dist/leadTaxonomy';
import {
  CLASSIFICATION_DIMENSIONS,
  optionList,
  resolveClassificationOptions,
} from '@valutten/shared-schemas/dist/leadClassification';
import type { ClassificationDimension, ClassificationOptions } from '@valutten/shared-schemas';
import { PageHeader } from '../../components/common/PageHeader';
import { CalendarRoutingSettings } from '../../components/leads/CalendarRoutingSettings';

type OutletCtx = {
  userData?: { uid?: string; companyId?: string; role?: string };
};

type RotationStrategy = 'round_robin' | 'least_loaded' | 'random' | 'first_available';

interface RotationRule {
  id: string;
  name: string;
  match: {
    // '' is a UI-only "not chosen yet" sentinel for new rotations. It is
    // never persisted: Save is disabled while any rotation still has it.
    // null = company-wide catch-all. A non-empty string = a category id.
    serviceCategory?: string | null;
    // Legacy single deal type. Under routing v2 (enforce) it is shown
    // read-only on rules that already carry it; new rules use the five
    // classification dimensions below instead.
    dealType?: string | null;
    officeId?: string | null;
    // Routing v2 classification constraints: the rule matches a lead whose
    // answer is one of these (situations: any overlap). Empty/absent = any.
    // Saved in every routing_v2 mode; they decide routing only under
    // enforce (off/observe: hidden from v1, observe logs the v2 diff).
    loanPurpose?: string[] | null;
    propertyUse?: string[] | null;
    borrowerSituation?: string[] | null;
    loanStructure?: string[] | null;
    // Loan size bands (the tenant's loanAmount options), e.g. "$5m or more".
    loanAmount?: string[] | null;
  };
  strategy: RotationStrategy;
  brokerIds: string[];
  slaOverrideMinutes?: number | null;
  passOffOverrideMinutes?: number | null;
}

interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
  timezone: string;
  mode: 'queue' | 'on_call' | 'assign_anyway';
  onCallBrokerIds: string[];
}

interface NotificationConfig { email: boolean; inApp: boolean; sms: boolean; }

interface RoutingConfig {
  rotations: RotationRule[];
  defaultSlaMinutes: number;
  defaultPassOffMinutes: number;
  defaultMaxOpenLeads: number;
  defaultStrategy: RotationStrategy;
  defaultMaxReassignments: number;
  defaultFallback: 'office_admin_queue' | 'random_assign' | 'queue_until_open';
  quietHours: QuietHours;
  notifyOnAssignment: NotificationConfig;
  geoRules: Array<{ id: string; states: string[]; officeId?: string | null; rotationRuleId?: string | null }>;
  geoFallback: 'company_default' | 'office_round_robin';
  cascadePriority?: LeadRoutingRules['cascadePriority'];
  // Explicit on/off for postcode territory routing. undefined = tenant has
  // never set it; isTerritoryRoutingEnabled then falls back to "a non-empty
  // territory list was already saved". Never default this to a boolean in
  // DEFAULT_CONFIG: the snapshot merge would turn a legacy territory tenant
  // off on the next save.
  territoryRoutingEnabled?: boolean;
  postcodeTerritories?: PostcodeTerritory[];
  territoryFallbackOfficeId?: string | null;
  // Hold time before retrying a lead that found nobody eligible. 0 disables.
  retryWhenNoneEligibleMinutes?: number;
  // Who catches a lead no rotation can take, in priority order. Optional so a
  // tenant that never set it is not sent an empty list on save.
  fallbackOwnerUids?: string[];
}

const DEFAULT_CONFIG: RoutingConfig = {
  rotations: [],
  defaultSlaMinutes: 15,
  defaultPassOffMinutes: 480,
  defaultMaxOpenLeads: 25,
  defaultStrategy: 'round_robin',
  defaultMaxReassignments: 3,
  defaultFallback: 'office_admin_queue',
  quietHours: {
    enabled: true,
    start: '22:00', end: '07:00',
    timezone: 'Australia/Sydney',
    mode: 'queue',
    onCallBrokerIds: [],
  },
  notifyOnAssignment: { email: true, inApp: true, sms: false },
  geoRules: [],
  geoFallback: 'company_default',
  retryWhenNoneEligibleMinutes: 15,
};

// Picker option shapes.
interface BrokerOption { id: string; displayName: string; officeIds: string[]; serviceIds: string[] | null; routingEnabled: boolean; }
interface CategoryOption { id: string; label: string; }

// Select sentinel for the company-wide (any category) catch-all. Maps to
// match.serviceCategory = null. Leading-underscore guarantees it can never
// collide with a real category id (ID_PATTERN forbids a leading underscore).
const COMPANY_WIDE = '__company_wide__';

// Known live service categories used when a tenant has no lead_taxonomy/default
// document yet. These mirror the categories actually captured in production.
const KNOWN_CATEGORIES: CategoryOption[] = [
  { id: 'home_loan', label: 'Home Loan' },
  { id: 'asset_finance', label: 'Asset Finance' },
  { id: 'buyers_advocacy', label: "Buyer's Advocacy" },
  { id: 'dm_inquiry', label: 'DM Inquiry' },
];

type RoutingV2Mode = 'off' | 'observe' | 'enforce';

const DIMENSION_LABEL: Record<ClassificationDimension, string> = {
  loanPurpose: 'Loan purpose',
  propertyUse: 'Property use',
  borrowerSituation: 'Borrower situation',
  loanStructure: 'Loan structure',
  loanAmount: 'Loan amount',
};

const CLEARED_DIMENSIONS = { loanPurpose: null, propertyUse: null, borrowerSituation: null, loanStructure: null, loanAmount: null };

function ruleClassificationCount(match: RotationRule['match']): number {
  return CLASSIFICATION_DIMENSIONS.filter((d) => (match[d] ?? []).length > 0).length;
}

function genId(): string {
  return `rot_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

const LeadRoutingRulesPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId ?? '';
  const role = userData?.role ?? '';
  // Marketing (Lead-Capture operator) is granted access to the 4 Lead-Capture
  // config tabs alongside super_admin / administrator (marketing RBAC §1 GRANT).
  const isAdmin = role === 'super_admin' || role === 'administrator' || role === 'marketing';

  const [config, setStoredConfig] = useState<RoutingConfig>(DEFAULT_CONFIG);
  const dirty = useRef(false);
  // Render-visible mirror of `dirty`: drives the save bar and the leave-page
  // warning. The ref stays the source for the snapshot listener, which must
  // not re-subscribe on every edit.
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const expectedUpdatedAt = useRef<number | null>(null);
  const setConfig = useCallback((update: React.SetStateAction<RoutingConfig>) => {
    dirty.current = true;
    setHasUnsaved(true);
    setStoredConfig(update);
  }, []);
  const [offices, setOffices] = useState<TerritoryOffice[]>([]);
  const [officeError, setOfficeError] = useState('');
  const [officesLoading, setOfficesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Distinct from save errors: set when the initial config snapshot fails to
  // load. While set, Save stays disabled so a click can't overwrite the stored
  // rotations with the empty DEFAULT_CONFIG.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Broker roster for the pickers (display name shown, Auth UID stored).
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [brokersLoading, setBrokersLoading] = useState(true);

  // Service category options (and per-category deal types) for the selects.
  const [categories, setCategories] = useState<CategoryOption[]>(KNOWN_CATEGORIES);
  const [dealTypesByCategory, setDealTypesByCategory] = useState<Record<string, CategoryOption[]>>({});
  // Classification options per category (tenant's own; defaults resolved at
  // render) and the tenant's routing_v2 mode, both from getLeadTaxonomy.
  const [classificationByCategory, setClassificationByCategory] = useState<Record<string, ClassificationOptions>>({});
  const [routingV2Mode, setRoutingV2Mode] = useState<RoutingV2Mode>('off');

  // --- Routing config snapshot
  useEffect(() => {
    if (!companyId || !isAdmin) return;
    dirty.current = false;
    setHasUnsaved(false);
    expectedUpdatedAt.current = null;
    const ref = doc(db, `companies/${companyId}/lead_routing_rules/default`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setLoadError(null);
        if (dirty.current) return; // Cursor updates must not discard unsaved map edits.
        expectedUpdatedAt.current = snap.data()?.updatedAt?.toMillis?.() ?? null;
        if (!snap.exists()) {
          setStoredConfig(DEFAULT_CONFIG);
          setLoading(false);
          return;
        }
        const data = snap.data() as Partial<RoutingConfig> | undefined;
        const merged = { ...DEFAULT_CONFIG, ...data } as RoutingConfig;
        setStoredConfig(merged);
        setLoading(false);
      },
      (err) => {
        console.error('[LeadRoutingRules] snapshot error', err);
        setLoadError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, [companyId, isAdmin]);

  useEffect(() => {
    if (!companyId || !isAdmin) return;
    let active = true;
    setOfficesLoading(true);
    setOfficeError('');
    const fn = httpsCallable<unknown, { offices: TerritoryOffice[] }>(functions, 'leadCaptureAction');
    fn({ action: 'getLeadRoutingOffices', companyId }).then(({ data }) => {
      if (!active) return;
      setOffices(data.offices.sort((a,b) => Number(!!b.isHeadOffice)-Number(!!a.isHeadOffice) || a.name.localeCompare(b.name)));
    }).catch((err: unknown) => {
      if (active) setOfficeError(err instanceof Error ? err.message : 'Could not load offices.');
    }).finally(() => { if (active) setOfficesLoading(false); });
    return () => { active = false; };
  }, [companyId, isAdmin]);

  // --- Broker roster (gated getBrokerLabels callable, the same source the routing
  // and assignment validators accept, so the picker can never offer a broker
  // the server would reject). Returns { id (=Auth UID), displayName, officeIds }
  // for active assignable members. One-shot; failures degrade to an empty list.
  useEffect(() => {
    if (!companyId || !isAdmin) return;
    let active = true;
    setBrokersLoading(true);
    const fn = httpsCallable<
      { companyId: string },
      { brokers: Array<{ id: string; displayName?: string; officeIds?: string[]; serviceIds?: string[] | null; routingEnabled?: boolean }> }
    >(functions, 'getBrokerLabels');
    fn({ companyId })
      .then((res) => {
        if (!active) return;
        const list = (res.data?.brokers ?? []).map((b) => ({
          id: b.id,
          officeIds: b.officeIds ?? [],
          serviceIds: b.serviceIds ?? null,
          routingEnabled: b.routingEnabled !== false,
          displayName: b.displayName?.trim() || b.id,
        }));
        list.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setBrokers(list);
        setBrokersLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error('[LeadRoutingRules] getBrokerLabels failed (non-fatal)', err);
        setBrokers([]);
        setBrokersLoading(false);
      });
    return () => { active = false; };
  }, [companyId, isAdmin]);

  // --- Service taxonomy (companies/{c}/lead_taxonomy/default). Readable by any
  // signed-in company member. Prefer the tenant's configured categories; fall
  // back to the known live set. One-shot; failures degrade to the known set.
  useEffect(() => {
    if (!companyId || !isAdmin) return;
    let active = true;
    const ref = doc(db, `companies/${companyId}/lead_taxonomy/default`);
    getDoc(ref)
      .then((snap) => {
        if (!active) return;
        if (!snap.exists()) {
          setCategories(KNOWN_CATEGORIES);
          setDealTypesByCategory({});
          return;
        }
        const data = snap.data() as {
          serviceCategories?: Array<{ id?: string; label?: string; order?: number; active?: boolean }>;
          dealTypes?: Record<string, Array<{ id?: string; label?: string; order?: number }>>;
        };
        const cats = (data.serviceCategories ?? [])
          .filter((c): c is { id: string; label?: string; order?: number; active?: boolean } =>
            !!c && typeof c.id === 'string' && c.active !== false)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((c) => ({ id: c.id, label: c.label?.trim() || c.id }));
        setCategories(cats.length > 0 ? cats : KNOWN_CATEGORIES);

        const dt: Record<string, CategoryOption[]> = {};
        if (data.dealTypes && typeof data.dealTypes === 'object') {
          for (const [key, arr] of Object.entries(data.dealTypes)) {
            if (!Array.isArray(arr)) continue;
            dt[key] = arr
              .filter((d): d is { id: string; label?: string; order?: number } =>
                !!d && typeof d.id === 'string')
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((d) => ({ id: d.id, label: d.label?.trim() || d.id }));
          }
        }
        setDealTypesByCategory(dt);
      })
      .catch((err) => {
        if (!active) return;
        console.warn('[LeadRoutingRules] lead_taxonomy read failed; using known categories', err);
        setCategories(KNOWN_CATEGORIES);
        setDealTypesByCategory({});
      });
    return () => { active = false; };
  }, [companyId, isAdmin]);

  // --- Classification options + routing_v2 mode. A failure leaves the page
  // reporting 'off' (classification rules shown as not yet in effect), which
  // is what the router does when it cannot read the switch.
  useEffect(() => {
    if (!companyId || !isAdmin) return;
    let active = true;
    const fn = httpsCallable<unknown, { classification?: Record<string, ClassificationOptions> | null; routingV2Mode?: RoutingV2Mode }>(
      functions, 'leadCaptureAction');
    fn({ action: 'getLeadTaxonomy', companyId })
      .then(({ data }) => {
        if (!active) return;
        setClassificationByCategory(data.classification ?? {});
        setRoutingV2Mode(data.routingV2Mode === 'enforce' || data.routingV2Mode === 'observe' ? data.routingV2Mode : 'off');
      })
      .catch((err) => {
        if (!active) return;
        console.warn('[LeadRoutingRules] classification options unavailable; routing v1 view', err);
      });
    return () => { active = false; };
  }, [companyId, isAdmin]);

  // Is postcode territory routing on? Same resolution the router and the save
  // handler use, so the toggle always shows what the server will actually do.
  const territoryEnabled = isTerritoryRoutingEnabled(config);

  const setTerritoryEnabled = useCallback((enabled: boolean) => {
    // Stored territories are deliberately kept when switching off: they stay a
    // draft the tenant can switch back on without redrawing the map.
    setConfig((c) => ({ ...c, territoryRoutingEnabled: enabled }));
  }, [setConfig]);

  const save = useCallback(async () => {
    if (!companyId) return;
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, { success: boolean }>(functions, 'leadCaptureAction');
      // Send the resolved on/off value, never an injected `postcodeTerritories: []`.
      // The old `?? []` put every saving tenant into territory mode, where an
      // uncovered postcode is returned unassigned on the spot. Stamping the
      // resolved boolean also makes a legacy tenant explicit without changing
      // their behaviour.
      await fn({
        action: 'saveLeadRoutingConfig',
        companyId,
        config: { ...config, territoryRoutingEnabled: territoryEnabled },
        expectedUpdatedAtMillis: expectedUpdatedAt.current,
      });
      const latest = await getDoc(doc(db, `companies/${companyId}/lead_routing_rules/default`));
      expectedUpdatedAt.current = latest.data()?.updatedAt?.toMillis?.() ?? null;
      dirty.current = false;
      setHasUnsaved(false);
      setStoredConfig({ ...DEFAULT_CONFIG, ...latest.data() } as RoutingConfig);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [companyId, config, territoryEnabled]);

  const addRotation = useCallback(() => {
    setConfig((c) => ({
      ...c,
      rotations: [...c.rotations, {
        id: genId(),
        name: 'New rotation',
        // serviceCategory '' = not chosen yet (forces an explicit pick).
        match: { serviceCategory: '', dealType: null, officeId: null },
        strategy: c.defaultStrategy,
        brokerIds: [],
        slaOverrideMinutes: null,
        passOffOverrideMinutes: null,
      }],
    }));
  }, [setConfig]);

  const updateRotation = useCallback((id: string, patch: Partial<RotationRule>) => {
    setConfig((c) => ({
      ...c,
      rotations: c.rotations.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));
  }, [setConfig]);

  const updateRotationMatch = useCallback((id: string, patch: Partial<RotationRule['match']>) => {
    setConfig((c) => ({
      ...c,
      rotations: c.rotations.map((r) => (r.id === id ? { ...r, match: { ...r.match, ...patch } } : r)),
    }));
  }, [setConfig]);

  const removeRotation = useCallback((id: string) => {
    setConfig((c) => ({ ...c, rotations: c.rotations.filter((r) => r.id !== id) }));
  }, [setConfig]);

  const summary = useMemo(() => {
    const ruleCount = config.rotations.length;
    const brokerCount = new Set(config.rotations.flatMap((r) => r.brokerIds)).size;
    return { ruleCount, brokerCount };
  }, [config]);

  // A rotation is incomplete if it has no brokers (schema requires >= 1) or no
  // service-category/company-wide choice ('' sentinel = not chosen yet).
  const hasInvalidRotation = useMemo(
    () => config.rotations.some((r) => r.match.serviceCategory === '' || r.brokerIds.length === 0),
    [config.rotations],
  );

  // Both preconditions belong to postcode routing only. A tenant that routes
  // by rotation alone was previously blocked from saving anything at all
  // whenever it had zero or several head offices.
  const missingHeadOffice = territoryEnabled && offices.filter((o) => o.isHeadOffice).length !== 1;
  const missingTerritoryRotation = territoryEnabled
    && (config.postcodeTerritories ?? []).map((t) => t.officeId)
      .some((id) => !config.rotations.some((r) => r.match.officeId === id));
  const saveBlockReason = officeError || (missingHeadOffice ? 'Set exactly one head office before saving postcode routing.' : null) || (missingTerritoryRotation ? 'Add an office rotation for each territory office before saving.' : null) || (loadError
    ? 'Routing configuration failed to load. Refresh before saving so your existing rules are not overwritten.'
    : hasInvalidRotation
      ? 'Every rotation needs a service category (or Company-wide) and at least one broker.'
      : null);

  const saveDisabled = saving || loading || officesLoading || !!saveBlockReason;

  // Warn before a reload or tab close drops unsaved routing edits. The app
  // uses BrowserRouter (no data router), so in-app navigation cannot be
  // blocked here; the always-visible save bar below covers that case.
  useEffect(() => {
    if (!hasUnsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsaved]);

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={GitBranch}
          title="Lead Routing Rules"
          subtitle="super_admin / administrator only"
        />
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-300 text-sm">
          You don&apos;t have permission to view this page. Contact your super_admin.
        </div>
      </div>
    );
  }

  return (
    <fieldset disabled={saving} className="space-y-6 min-w-0">
      <PageHeader
        icon={GitBranch}
        title="Lead Routing Rules"
        subtitle="Choose office territories and the teams that handle each service."
        badge="Beta"
        actions={
          <button
            onClick={save}
            disabled={saveDisabled}
            title={saveBlockReason ?? undefined}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Routing'}
          </button>
        }
      />

      {loadError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Could not load your saved routing configuration ({loadError}). Saving is disabled so your existing
            rules are not overwritten. Refresh the page to retry.
          </span>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
        </div>
      )}
      {!loading && config.notifyOnAssignment.email === false && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Broker assignment emails are OFF. Brokers will NOT be notified of new leads (they must check the inbox
            manually). The SLA clock still runs.
          </span>
        </div>
      )}
      {!loadError && hasInvalidRotation && !loading && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Some rotations are incomplete. Each rotation needs a service category (or Company-wide) and at least
            one broker. Saving is disabled until every rotation is complete.
          </span>
        </div>
      )}
      {savedAt && Date.now() - savedAt < 3000 && (
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">
          Routing saved.
        </div>
      )}

      {officeError && <p role="alert" className="text-sm text-red-300">Could not load offices: {officeError}. Refresh to retry.</p>}
      {missingTerritoryRotation && <p role="alert" className="text-sm text-amber-300">Add an office rotation for every territory office before saving.</p>}

      {/* Explicit on/off for postcode territory routing. Off is the default for
          a tenant that has never configured territories, and the map below stays
          available as a draft so switching back on costs nothing. */}
      {!loading && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4 space-y-2">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={territoryEnabled}
              onChange={(e) => setTerritoryEnabled(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-600"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-white">
                Route leads by postcode territory
              </span>
              <span className="block text-xs text-gray-400">
                {territoryEnabled
                  ? 'On. The postcode decides which office owns the lead, then that office’s rotation picks the broker. A postcode no territory covers goes to your head office for manual triage, so set exactly one head office and give every territory office a rotation.'
                  : 'Off. Leads are routed by your rotation rules below (service, deal type and office). Any territories you have drawn are kept as a draft and are not used.'}
              </span>
            </span>
          </label>
        </div>
      )}

      {!loading && !officesLoading && !officeError && <fieldset disabled={saving} className="min-w-0">
        <LeadTerritoryEditor disabled={saving} offices={offices} territories={config.postcodeTerritories ?? []}
          fallbackOfficeId={config.territoryFallbackOfficeId ?? null} rotations={config.rotations}
          cascadePriority={config.cascadePriority}
          routingV2Mode={routingV2Mode}
          onChange={(postcodeTerritories, territoryFallbackOfficeId) => setConfig((c) => ({ ...c, postcodeTerritories, territoryFallbackOfficeId }))} />
      </fieldset>}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Rotation Rules</div>
          <div className="mt-1 text-3xl font-bold text-white">{summary.ruleCount}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Brokers In Rotation</div>
          <div className="mt-1 text-3xl font-bold text-white">{summary.brokerCount}</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">SLA</div>
          <div className="mt-1 text-3xl font-bold text-white">{config.defaultSlaMinutes}<span className="text-base text-gray-400 ml-1">min</span></div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Quiet Hours</div>
          <div className="mt-1 text-base font-semibold text-white">
            {config.quietHours.enabled ? `${config.quietHours.start}-${config.quietHours.end}` : 'Disabled'}
          </div>
          <div className="mt-1 text-[11px] text-gray-400">{config.quietHours.timezone}</div>
        </div>
      </div>

      {/* Rotation rules list */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">Rotation Rules (cascade by specificity)</h3>
          <button
            onClick={addRotation}
            className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 hover:bg-gray-700 text-white text-xs inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add Rotation
          </button>
        </div>

        {routingV2Mode !== 'enforce' && (
          <div
            role="note"
            data-testid="routing-v2-not-enforced-note"
            className="mb-3 flex items-start gap-2 rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200"
          >
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              You can save rotations that match on lead answers (loan purpose, property use, situation, loan structure) now.
              They only take effect once classification routing is enforced for your company. Until then those rotations are
              skipped and leads are routed by service, deal type and office. Ask VALUTTEN support when you are ready to switch it on.
            </span>
          </div>
        )}

        {config.rotations.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-700 bg-gray-900/20 p-8 text-center text-gray-400 text-sm">
            No rotation rules yet. Click <strong>Add Rotation</strong> to create one for a service category (or company-wide).
          </div>
        ) : (
          <div className="space-y-3">
            {config.rotations.map((r) => {
              const scStored = r.match.serviceCategory;
              // '' -> placeholder; null/undefined -> company-wide; id -> id.
              const scValue = scStored === '' ? '' : (scStored == null ? COMPANY_WIDE : scStored);
              // Guarantee the stored category is always selectable (round-trips a
              // live value even when it is absent from the taxonomy list).
              const extraCat = (typeof scStored === 'string' && scStored !== '' &&
                !categories.some((c) => c.id === scStored))
                ? [{ id: scStored, label: scStored }]
                : [];
              const catOpts = [...categories, ...extraCat];

              const dtStored = r.match.dealType ?? null;
              const availableDt = (typeof scStored === 'string' && scStored !== '')
                ? (dealTypesByCategory[scStored] ?? [])
                : [];
              const extraDt = (typeof dtStored === 'string' && dtStored !== '' &&
                !availableDt.some((d) => d.id === dtStored))
                ? [{ id: dtStored, label: dtStored }]
                : [];
              const dtOpts = [...availableDt, ...extraDt];
              // Under routing v2 the deal type is legacy: shown read-only on a
              // rule that already has one, never offered for new rules.
              const showDealType = routingV2Mode !== 'enforce' && dtOpts.length > 0;
              const classificationOptions = typeof scStored === 'string' && scStored !== ''
                ? resolveClassificationOptions(classificationByCategory, scStored)
                : null;
              const classifiedDims = ruleClassificationCount(r.match);

              const missing: string[] = [];
              if (scStored === '') missing.push('Choose a service category (or Company-wide).');
              if (r.brokerIds.length === 0) missing.push('Add at least one broker.');
              if (classifiedDims > 0 && !scStored) {
                missing.push('Classification needs a service category.');
              }

              return (
                <div key={r.id} className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
                  <div className="flex justify-between items-start gap-3 mb-3">
                    <input
                      type="text"
                      value={r.name}
                      onChange={(e) => updateRotation(r.id, { name: e.target.value })}
                      className="bg-gray-800 border border-gray-700 text-white rounded-md px-3 py-1.5 text-sm font-semibold flex-1"
                      placeholder="Rotation name (e.g. Home Loan > First Home Buyer)"
                    />
                    <button
                      type="button"
                      onClick={() => removeRotation(r.id)}
                      className="text-gray-400 hover:text-red-400 p-1.5"
                      title="Remove rotation"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Service Category</label>
                      <select
                        value={scValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          const next = v === '' ? '' : v === COMPANY_WIDE ? null : v;
                          // Changing category invalidates any deal-type or
                          // classification sub-routing (options are per category).
                          updateRotationMatch(r.id, { serviceCategory: next, dealType: null, ...CLEARED_DIMENSIONS });
                        }}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                      >
                        <option value="" disabled>Choose a category…</option>
                        <option value={COMPANY_WIDE}>Company-wide (any category)</option>
                        {catOpts.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <label className="block text-xs text-gray-400">Office
                      <select aria-label={`Office for ${r.name}`} value={r.match.officeId ?? ''}
                        onChange={(e) => updateRotationMatch(r.id, { officeId: e.target.value || null })}
                        className="mt-1 w-full rounded-md border border-gray-700 bg-gray-800 px-2 py-1 text-sm">
                        <option value="">Any office</option>
                        {r.match.officeId && !offices.some((o) => o.id === r.match.officeId) && <option value={r.match.officeId}>Unavailable office</option>}
                        {offices.map((o) => <option key={o.id} value={o.id}>{o.name}{o.isHeadOffice ? ' · HQ' : ''}</option>)}
                      </select>
                    </label>
                    {routingV2Mode === 'enforce' && dtStored && (
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Deal Type (legacy)</span>
                        <div className="flex items-center gap-2 text-xs text-gray-300">
                          <span className="rounded border border-gray-700 bg-gray-800 px-2 py-1">
                            {dtOpts.find((d) => d.id === dtStored)?.label ?? dtStored}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateRotationMatch(r.id, { dealType: null })}
                            className="text-gray-400 hover:text-red-400 inline-flex items-center gap-1"
                            title="Remove the legacy deal type and use the classification questions instead"
                          >
                            <X className="w-3 h-3" /> Remove
                          </button>
                        </div>
                      </div>
                    )}
                    {showDealType && (
                      <div>
                        <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Deal Type (optional)</label>
                        <select
                          value={dtStored ?? ''}
                          onChange={(e) => updateRotationMatch(r.id, { dealType: e.target.value || null })}
                          className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                        >
                          <option value="">Any deal type</option>
                          {dtOpts.map((d) => (
                            <option key={d.id} value={d.id}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {classificationOptions && (
                    <div className="mb-3 space-y-2" data-testid={`rotation-classification-${r.id}`}>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500">
                        Match leads by their answers (optional; none ticked means any answer)
                      </div>
                      {CLASSIFICATION_DIMENSIONS.map((dim) => {
                        const opts = optionList(classificationOptions, dim).filter((o) => o.active !== false)
                          .slice().sort((a, b) => a.order - b.order);
                        const selected = r.match[dim] ?? [];
                        const stale = selected.filter((v) => !opts.some((o) => o.id === v));
                        if (opts.length === 0 && stale.length === 0) return null;
                        const toggle = (id: string, on: boolean) => {
                          const next = on ? [...selected, id] : selected.filter((v) => v !== id);
                          updateRotationMatch(r.id, { [dim]: next.length > 0 ? next : null });
                        };
                        return (
                          <fieldset key={dim} className="min-w-0">
                            <legend className="text-[11px] text-gray-400 mb-1">{DIMENSION_LABEL[dim]}</legend>
                            <div className="flex flex-wrap gap-1.5">
                              {opts.map((o) => {
                                const on = selected.includes(o.id);
                                return (
                                  <label
                                    key={o.id}
                                    className={`relative inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] cursor-pointer ${on ? 'border-blue-500 bg-blue-500/15 text-blue-200' : 'border-gray-700 text-gray-300 hover:border-gray-500'}`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={on}
                                      onChange={(e) => toggle(o.id, e.target.checked)}
                                    />
                                    {o.label}
                                  </label>
                                );
                              })}
                              {stale.map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => toggle(v, false)}
                                  className="inline-flex items-center gap-1 rounded-full border border-amber-600 px-2 py-0.5 text-[11px] text-amber-300"
                                  title="This option is no longer offered for the service. Remove it before saving."
                                >
                                  {v} <X className="w-3 h-3" />
                                </button>
                              ))}
                            </div>
                          </fieldset>
                        );
                      })}
                    </div>
                  )}
                  {routingV2Mode !== 'enforce' && classifiedDims > 0 && (
                    <div
                      data-testid={`rotation-classification-inactive-${r.id}`}
                      className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-300"
                    >
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      <span>
                        Not in effect yet. This rotation matches on lead answers, which only apply once classification routing is enforced for your company. Until then this rotation is skipped and leads go to your other rotations.
                      </span>
                      <button
                        type="button"
                        onClick={() => updateRotationMatch(r.id, { ...CLEARED_DIMENSIONS })}
                        className="underline hover:text-amber-200"
                      >
                        Remove classification
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Strategy</label>
                      <select
                        value={r.strategy}
                        onChange={(e) => updateRotation(r.id, { strategy: e.target.value as RotationStrategy })}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                      >
                        <option value="round_robin">Round-robin</option>
                        <option value="least_loaded">Least-loaded</option>
                        <option value="random">Random</option>
                        <option value="first_available">First-available</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">SLA Override (min)</label>
                      <input
                        type="number"
                        min={1}
                        value={r.slaOverrideMinutes ?? ''}
                        onChange={(e) => updateRotation(r.id, { slaOverrideMinutes: e.target.value ? parseInt(e.target.value, 10) : null })}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                        placeholder="inherits"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Pass-Off Override (min)</label>
                      <input
                        type="number"
                        min={1}
                        value={r.passOffOverrideMinutes ?? ''}
                        onChange={(e) => updateRotation(r.id, { passOffOverrideMinutes: e.target.value ? parseInt(e.target.value, 10) : null })}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs"
                        placeholder="inherits"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Staff in rotation (assignment order)</label>
                    <BrokerMultiSelect
                      brokers={brokers.filter(b => b.routingEnabled &&
                        (!r.match.officeId || b.officeIds.includes(r.match.officeId)) &&
                        (b.serviceIds === null || (!!r.match.serviceCategory && b.serviceIds.includes(r.match.serviceCategory))))}
                      loading={brokersLoading}
                      selected={r.brokerIds}
                      onChange={(ids) => updateRotation(r.id, { brokerIds: ids })}
                      ordered
                    />
                    <div className="text-[10px] text-gray-500 mt-1">
                      {r.brokerIds.length} broker{r.brokerIds.length === 1 ? '' : 's'} in this rotation. Order is the round-robin sequence.
                    </div>
                  </div>

                  {missing.length > 0 && (
                    <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-300">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{missing.join(' ')}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Globals */}
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5 space-y-3">
        <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">Global Defaults</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="SLA (min)">
            <input type="number" min={1} value={config.defaultSlaMinutes}
              onChange={(e) => setConfig((c) => ({ ...c, defaultSlaMinutes: parseInt(e.target.value, 10) || 15 }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
          </Field>
          <Field label="Pass-off after (min)">
            <input type="number" min={1} value={config.defaultPassOffMinutes}
              onChange={(e) => setConfig((c) => ({ ...c, defaultPassOffMinutes: parseInt(e.target.value, 10) || 480 }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
          </Field>
          <Field label="Max open leads / broker">
            <input type="number" min={1} value={config.defaultMaxOpenLeads}
              onChange={(e) => setConfig((c) => ({ ...c, defaultMaxOpenLeads: parseInt(e.target.value, 10) || 25 }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
          </Field>
          <Field label="Max reassignments">
            <input type="number" min={1} max={10} value={config.defaultMaxReassignments}
              onChange={(e) => setConfig((c) => ({ ...c, defaultMaxReassignments: parseInt(e.target.value, 10) || 3 }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Default strategy">
            <select value={config.defaultStrategy}
              onChange={(e) => setConfig((c) => ({ ...c, defaultStrategy: e.target.value as RotationStrategy }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs">
              <option value="round_robin">Round-robin</option>
              <option value="least_loaded">Least-loaded</option>
              <option value="random">Random</option>
              <option value="first_available">First-available</option>
            </select>
          </Field>
          <Field label="Final fallback">
            <select value={config.defaultFallback}
              onChange={(e) => setConfig((c) => ({ ...c, defaultFallback: e.target.value as RoutingConfig['defaultFallback'] }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs">
              <option value="office_admin_queue">Office admin queue</option>
              <option value="random_assign">Random assign anyway (ignores working hours)</option>
              <option value="queue_until_open">Hold for business hours</option>
            </select>
          </Field>
        </div>
        <div data-testid="routing-fallback-owners">
          <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">
            If no rotation can take a lead, assign it to
          </label>
          <BrokerMultiSelect
            brokers={brokers}
            loading={brokersLoading}
            selected={config.fallbackOwnerUids ?? []}
            onChange={(ids) => setConfig((c) => ({ ...c, fallbackOwnerUids: ids }))}
            ordered
          />
          <p className="mt-1 text-[11px] text-gray-400">
            In order: the lead goes to the first person here who is active and not on leave.
            Used when no rotation matches the lead, or every broker in the matched rotation
            stayed unavailable after the retries. Usually a super admin or administrator.
          </p>
          {(config.fallbackOwnerUids ?? []).length === 0 && (
            <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-300">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Nobody chosen. A lead no rotation can take stays unassigned until someone in the Inbox picks it up.</span>
            </div>
          )}
        </div>
      </div>

      {/* Quiet hours */}
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5 space-y-3">
        <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">Quiet Hours</h3>
        <p className="text-xs text-gray-400">Lead arrives at 11pm Sydney: what should happen? Configurable per subscriber.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Enabled">
            <input type="checkbox" checked={config.quietHours.enabled}
              onChange={(e) => setConfig((c) => ({ ...c, quietHours: { ...c.quietHours, enabled: e.target.checked } }))}
              className="accent-blue-500 w-4 h-4" />
          </Field>
          <Field label="Start">
            <input type="time" value={config.quietHours.start}
              onChange={(e) => setConfig((c) => ({ ...c, quietHours: { ...c.quietHours, start: e.target.value } }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
          </Field>
          <Field label="End">
            <input type="time" value={config.quietHours.end}
              onChange={(e) => setConfig((c) => ({ ...c, quietHours: { ...c.quietHours, end: e.target.value } }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
          </Field>
          <Field label="Mode">
            <select value={config.quietHours.mode}
              onChange={(e) => setConfig((c) => ({ ...c, quietHours: { ...c.quietHours, mode: e.target.value as QuietHours['mode'] } }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs">
              <option value="queue">Queue until next business hour</option>
              <option value="on_call">Assign to on-call broker</option>
              <option value="assign_anyway">Assign anyway (alarm fires SLA)</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Timezone">
            <select value={config.quietHours.timezone}
              onChange={(e) => setConfig((c) => ({ ...c, quietHours: { ...c.quietHours, timezone: e.target.value } }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs">
              {AU_TIMEZONE_VALUES.map((tz) => (
                <option key={tz} value={tz}>{AU_TIMEZONE_LABELS[tz]}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              Quiet hours are read in this zone. It is also the zone used for any broker
              who has not set one of their own on My Availability.
            </p>
          </Field>
          <Field label="Retry when nobody is available (min)">
            <input type="number" min={0} max={1440}
              value={config.retryWhenNoneEligibleMinutes ?? 15}
              onChange={(e) => setConfig((c) => ({
                ...c,
                retryWhenNoneEligibleMinutes: e.target.value === ''
                  ? 15
                  : Math.max(0, Math.min(1440, parseInt(e.target.value, 10) || 0)),
              }))}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-md px-2 py-1 text-xs" />
            <p className="mt-1 text-[11px] text-gray-400">
              When every broker in the matched rotation is on leave, paused, off shift or
              at capacity, hold the lead this long and try again. 0 leaves it unassigned
              immediately for an admin to pick up.
            </p>
          </Field>
        </div>
        {config.quietHours.mode === 'on_call' && (
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">On-call brokers</label>
            <BrokerMultiSelect
              brokers={brokers}
              loading={brokersLoading}
              selected={config.quietHours.onCallBrokerIds}
              onChange={(ids) =>
                setConfig((c) => ({ ...c, quietHours: { ...c.quietHours, onCallBrokerIds: ids } }))
              }
            />
            {config.quietHours.onCallBrokerIds.length === 0 && (
              <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-300">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>No on-call brokers selected. Quiet-hours leads will fall through to the final fallback.</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notifications */}
      <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-5 space-y-3">
        <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold">Assignment Notifications</h3>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Email (Resend)">
            <input type="checkbox" checked={config.notifyOnAssignment.email}
              onChange={(e) => {
                const next = e.target.checked;
                // Turning email OFF silences every broker assignment notification
                // company-wide, so require an explicit confirm. Turning it back ON
                // needs no confirm.
                if (!next && !window.confirm('Turn OFF broker assignment emails for the whole company? Brokers will stop being notified of new leads. Are you sure?')) {
                  return;
                }
                setConfig((c) => ({ ...c, notifyOnAssignment: { ...c.notifyOnAssignment, email: next } }));
              }}
              className="accent-blue-500 w-4 h-4" />
          </Field>
          <Field label="In-app realtime (not yet wired)">
            {/* Dead config: notifyOnAssignment.inApp is consumed nowhere at
                runtime. Render read-only (mirrors the deferred SMS control) so
                nobody believes it does anything, but keep the stored value so the
                field is preserved on save. */}
            <input type="checkbox" disabled readOnly checked={config.notifyOnAssignment.inApp}
              className="accent-blue-500 w-4 h-4 opacity-50" />
          </Field>
          <Field label="SMS (deferred, paid add-on)">
            <input type="checkbox" disabled checked={false}
              className="accent-blue-500 w-4 h-4 opacity-50" />
          </Field>
        </div>
      </div>

      {/* Calendar-aware routing (Phase 3a). Its settings live in their own
          document and callable, so it saves independently of the rules
          above and is unaffected by this page's optimistic lock. */}
      <CalendarRoutingSettings companyId={companyId} />

      {loading && (
        <div className="text-center text-gray-400 text-sm py-4">Loading routing config…</div>
      )}

      {/* The header button scrolls away on this long page (map, rotations,
          defaults), so unsaved edits get a save bar pinned to the bottom of
          the scroll area until they are saved. */}
      {hasUnsaved && (
        <div
          data-testid="routing-unsaved-bar"
          className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-500/40 bg-gray-900/95 p-3 shadow-lg backdrop-blur"
        >
          <span className="text-sm text-gray-200">
            {saveBlockReason ?? 'You have unsaved routing changes.'}
          </span>
          <button
            type="button"
            onClick={save}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Routing'}
          </button>
        </div>
      )}
    </fieldset>
  );
};

/**
 * Multi-select for brokers. Shows display names, stores Auth UIDs. When
 * `ordered` is set the selection order is preserved and surfaced (round-robin
 * sequence) with reorder controls. Selected UIDs not present in the roster
 * (e.g. a member who has since left) are still shown so they can be removed
 * rather than silently retained.
 */
const BrokerMultiSelect: React.FC<{
  brokers: BrokerOption[];
  loading: boolean;
  selected: string[];
  onChange: (ids: string[]) => void;
  ordered?: boolean;
}> = ({ brokers, loading, selected, onChange, ordered = false }) => {
  const [filter, setFilter] = useState('');

  const byId = useMemo(() => {
    const m = new Map<string, BrokerOption>();
    brokers.forEach((b) => m.set(b.id, b));
    return m;
  }, [brokers]);

  const toggle = useCallback((id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }, [selected, onChange]);

  const move = useCallback((index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= selected.length) return;
    const next = selected.slice();
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    onChange(next);
  }, [selected, onChange]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return brokers;
    return brokers.filter((b) => b.displayName.toLowerCase().includes(q));
  }, [brokers, filter]);

  const labelFor = (id: string) => byId.get(id)?.displayName ?? id;

  return (
    <div className="space-y-2">
      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id, idx) => {
            const known = byId.has(id);
            return (
              <span
                key={id}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                  known
                    ? 'border-blue-500/40 bg-blue-500/10 text-blue-200'
                    : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                }`}
              >
                {ordered && <span className="text-[10px] font-mono text-gray-400">{idx + 1}.</span>}
                <span>{labelFor(id)}</span>
                {!known && <span className="text-[9px] uppercase tracking-wide text-amber-300/80">not in roster</span>}
                {ordered && (
                  <>
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="text-gray-400 hover:text-white disabled:opacity-30"
                      title="Move earlier"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === selected.length - 1}
                      className="text-gray-400 hover:text-white disabled:opacity-30"
                      title="Move later"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  className="text-gray-400 hover:text-red-300"
                  title="Remove broker"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="text-[11px] text-gray-500">No brokers selected yet. Tick names below to add them.</div>
      )}

      <div className="rounded-md border border-gray-700 bg-gray-900/40">
        {brokers.length > 8 && (
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search brokers by name…"
            className="w-full bg-gray-800 border-b border-gray-700 text-white rounded-t-md px-2 py-1 text-xs focus:outline-none"
          />
        )}
        <div className="max-h-40 overflow-y-auto p-1">
          {loading ? (
            <div className="px-2 py-3 text-xs text-gray-500">Loading brokers…</div>
          ) : brokers.length === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">No assignable brokers found for this company.</div>
          ) : filtered.length === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">No brokers match your search.</div>
          ) : (
            filtered.map((b) => (
              <label
                key={b.id}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(b.id)}
                  onChange={() => toggle(b.id)}
                  className="accent-blue-500 w-3.5 h-3.5"
                />
                <span className="text-xs text-gray-200">{b.displayName}</span>
              </label>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

export default LeadRoutingRulesPage;
